import { Check, Pencil, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { Markdown } from '@/components/Markdown'
import { Button } from '@/components/ui/button'
import { Panel } from '@/components/ui/display'
import { Textarea } from '@/components/ui/field'
import { NoteVisibility } from './NoteVisibility'
import type { MeetingNote as NoteModel } from '@/data/types'

const MAX_LENGTH = 20_000

/**
 * One extracted note.
 *
 * External visibility is a property of the note, not of the meeting: the same
 * transcript yields a client update that should be shared and a risk log that
 * should not. The toggle therefore sits in the note header, where the text it
 * governs is on screen while the decision is made.
 */
export function NoteCard({
  note,
  onEdit,
  onToggleShared,
  onRemove,
  busy,
}: {
  note: NoteModel
  onEdit: (body: string) => void
  onToggleShared: (shared: boolean) => void
  onRemove: () => void
  busy?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note.body)

  return (
    <Panel>
      <div className="group flex items-center gap-2 border-b border-[var(--color-border-subtle)] px-3 py-1.5">
        <span className="min-w-0 flex-1">
          <h4 className="truncate text-xs font-medium text-[var(--color-fg)]">{note.title}</h4>
          {/* A one-off note is named after its instruction, which is a lossy
              summary of it — so the instruction itself stays visible. */}
          {note.prompt ? (
            <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
              {note.prompt}
            </span>
          ) : null}
        </span>

        <NoteVisibility
          title={note.title}
          shared={note.sharedExternally}
          busy={busy}
          onChange={onToggleShared}
        />

        {/* Always visible, not hover-revealed: a generated note is a first draft
            that people are expected to correct, so hiding the pencil until
            hover made the notes read as generated output you cannot touch. */}
        <div className="flex shrink-0 items-center gap-0.5">
          {!editing ? (
            <Button
              size="iconSm"
              variant="ghost"
              disabled={busy}
              aria-label={`Edit ${note.title}`}
              onClick={() => {
                setDraft(note.body)
                setEditing(true)
              }}
            >
              <Pencil className="size-3" />
            </Button>
          ) : null}
          <Button
            size="iconSm"
            variant="ghost"
            disabled={busy}
            aria-label={`Remove ${note.title}`}
            className="hover:text-[var(--color-danger)]"
            onClick={onRemove}
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>

      <div className="px-3 py-2.5">
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              rows={10}
              maxLength={MAX_LENGTH}
              aria-label={`${note.title} text`}
              className="font-mono text-xs"
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-2xs text-[var(--color-fg-subtle)]">
                {draft.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
              </span>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="size-3.5" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={draft.trim().length === 0}
                  onClick={() => {
                    onEdit(draft.trim())
                    setEditing(false)
                  }}
                >
                  <Check className="size-3.5" />
                  Save
                </Button>
              </div>
            </div>
          </div>
        ) : note.body ? (
          // Rendered text stays selectable — the pencil is the way in, so a
          // stray click while copying a figure does not drop you into a editor.
          <Markdown className="prose-chat text-xs text-[var(--color-fg)]">{note.body}</Markdown>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setDraft('')
              setEditing(true)
            }}
            className="w-full text-left text-2xs text-[var(--color-fg-subtle)] outline-none hover:text-[var(--color-fg-muted)]"
          >
            {note.pending
              ? 'Runs once the meeting has been recorded — or write it yourself.'
              : note.prompt
                ? // Honest about the gap rather than pretending it is queued:
                  // no model is wired up behind this yet.
                  'The model is not connected yet — write it yourself for now.'
                : 'Nothing written yet — click to write it.'}
          </button>
        )}
      </div>
    </Panel>
  )
}
